'use client'
// Favourites on Supabase (2026-08-26): the Clerk-era button was invisible to every
// visitor since the static cutover (permanently signed-out auth shim) and its server
// action wrote to the retired Mongo. Favourites now key on the CONTENT PATH and live
// in user_preferences kind='bookmark'; the dashboard lists them. Members-only, as before.
// PostHog (fix 6, 2026-10-16): one `favButtonClicked` per add/remove that reached the
// database, payload from lib/analytics/favourites.ts ({ action, content_type, content_path,
// kind }); the Clerk-era collection/metadata properties are gone.
import { Icon } from '@/ui/atoms/Icon'
import { useSupabaseAuth } from '@/context/supabaseContext'
import { toggleBookmark } from '@/lib/supabase/engagement'
import { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { useFavouriteContext } from '@/context/favouriteContext'
import { useUserFavourites } from '@/context/userFavouritesContext'
import { ErrorBoundary } from 'react-error-boundary'
import { useToast } from '@/ui/components/Toast/use-toast'
import { sendGAEvent } from '@next/third-parties/google'
import { trackEvent } from '@/lib/analytics/posthog'
import { favouriteEventAfterWrite } from '@/lib/analytics/favourites'
import { recordMemberActivity } from '@/lib/analytics/memberActivity'

interface FavouriteClientButtonProps {
  initialFavorite?: boolean
  showBackground?: boolean
  hasImage?: boolean
}

export default function FavouriteClientButton({
  initialFavorite = false,
  showBackground = false,
  hasImage = false,
}: FavouriteClientButtonProps) {
  const { user, isLoaded } = useSupabaseAuth()
  const { collection, path: ctxPath, title: ctxTitle } = useFavouriteContext()
  const pathname = usePathname()
  const { userFavouriteIds, isLoading, addFavourite, removeFavourite } = useUserFavourites()
  const { toast } = useToast()

  // Cards pass their target path via context; on detail pages the button gates
  // the page itself, so the current pathname IS the favourite.
  const path = ctxPath || pathname || ''

  const [isFavourite, setIsFavourite] = useState(
    () => initialFavorite || userFavouriteIds.includes(path),
  )

  useEffect(() => {
    if (!isLoading && path) {
      setIsFavourite(userFavouriteIds.includes(path))
    }
  }, [userFavouriteIds, path, isLoading])

  // Members-only, same as the Clerk-era behavior
  if (!isLoaded || !user || !path) {
    return null
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault() // Prevent default behavior to stop link

    const newFavouriteState = !isFavourite
    setIsFavourite(newFavouriteState)
    if (newFavouriteState) {
      addFavourite(path)
    } else {
      removeFavourite(path)
    }

    // Persist in background (optimistic UI above); PostHog hears about it once stored
    const title = ctxTitle || (typeof document !== 'undefined' ? document.title : path)
    void toggleBookmark(path, title)
      .then((stored) => {
        const event = favouriteEventAfterWrite({
          path,
          wanted: newFavouriteState,
          stored,
          kind: 'bookmark',
        })
        if (event) {
          trackEvent(event.name, event.properties)
          // only a save/unsave that reached the database (EA's own record, member_activity)
          void recordMemberActivity(event.properties.action === 'add' ? 'save' : 'unsave', {
            target: event.properties.content_path,
          })
        }
      })
      .catch((err) => console.error('Failed to save favourite:', err))

    // Analytics (GA loads only with consent)
    sendGAEvent('event', 'favButtonClicked', {
      collection: collection,
      isNowFav: newFavouriteState,
    })

    toast({
      icon: newFavouriteState ? 'heart-3-filled' : 'heart-3-line',
      title: newFavouriteState ? 'Added to favorites' : 'Removed from favorites',
      description: 'You can find your favorites on your dashboard!',
      duration: 5000,
    })
  }

  const buttonContent = (
    <button className={`w-full h-full flex items-center justify-center`} onClick={handleClick}>
      <Icon
        name={isFavourite ? 'heart-3-filled' : 'heart-3-line'}
        className={`${isLoading ? 'animate-pulse' : ''}`}
      />
    </button>
  )

  if (showBackground) {
    return (
      <ErrorBoundary fallback={<div></div>}>
        <div
          className={`absolute top-2 right-2 w-8 h-8 rounded-full canhover:hover:bg-bg-subtle group backdrop-blur-sm flex items-center justify-center transition-colors pointer-events-auto ${hasImage ? 'text-white canhover:hover:bg-white canhover:hover:text-black bg-[#000000] bg-opacity-20' : 'bg-bg-subtle text-text-base canhover:hover:bg-neutral-90'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {buttonContent}
        </div>
      </ErrorBoundary>
    )
  }

  return <ErrorBoundary fallback={<div></div>}>{buttonContent}</ErrorBoundary>
}
